import type { Session } from '@/types/session.types';
import GOGovLogo from '@/go.gov/components/layout/GOGovLogo';
import { HEADER_HEIGHT } from '@/ui/Layout/Header/HeaderBar';
import { hubThemeLight } from '@/ui/theme';
import { CustomThemeProvider } from '@/ui/theme/ThemeContext';
import { clearLocalCognitoSession } from '@/utils/auth/clearMfaSessionSatisfaction';
import { setCallback } from '@/utils/auth/callbacks';
import { logLogoutActivity } from '@/utils/auth/logLogoutActivity';
import { generateColor } from '@/utils/colors';
import { IS_ETS_NEXT_PROD } from '@/utils/constants';
import useSession from '@/utils/hooks/useSession';
import { stopImpersonatingUser } from '@/utils/impersonation';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { MouseEvent, ReactElement, useState } from 'react';

function GoGovHeaderBar(): ReactElement {
  const router = useRouter();
  const { user, isImpersonating }: Session = useSession() as Session;

  const clearImpersonatedUser = () => {
    stopImpersonatingUser();
    router.reload();
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const closeMenu = (): void => {
    setAnchorEl(null);
  };

  const logoutClick = async (): Promise<void> => {
    await logLogoutActivity({
      reason: 'gogov-header',
      userEmail: user?.email,
    });
    try {
      // Amplify signOut alone does not bump the in-memory ID-token keep-alive
      // in _app. clearLocalCognitoSession calls signOutUser, then clears that
      // cache, the token store, and cookies. hubAmplifySignOut is the Hub
      // header path (logout log + MFA last-satisfied POST).
      await clearLocalCognitoSession();
    } catch {
      // Token wipe should not block navigation to login.
    }
    stopImpersonatingUser();
    setCallback(router.asPath);
    router.push(`/login`);
  };

  const handleOpenMenu = (e: MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(e.currentTarget);
  };

  return (
    <CustomThemeProvider initialSettings={{ themeOverride: hubThemeLight }}>
      <AppBar
        position='sticky'
        elevation={0}
        sx={{
          height: HEADER_HEIGHT,
          display: 'flex',
          flexDirection: 'row',
          zIndex: 'drawer',
          bgcolor: 'rgba(255,255,255,0)',
        }}
        color='default'
      >
        <Button
          aria-label='Go to hub homepage'
          onClick={() => router.push('/hub')}
          sx={{
            minWidth: 0,
            p: 0,
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <GOGovLogo />
        </Button>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box>
            {/* <IconButton
              component='a'
              href={
                IS_ETS_NEXT_PROD
                  ? 'https://gsaetsnexttest.servicenowservices.com/$sn-va-web-client-app.do'
                  : 'https://gsaetsnexttest.servicenowservices.com/$sn-va-web-client-app.do'
              }
              target='_blank'
              rel='noopener noreferrer'
              aria-label='Open Chat Bot'
              sx={{
                padding: 1,
                marginRight: 2,
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <Image
                src='/assets/goGovChatBot.png'
                alt='Chat Bot'
                width={HEADER_HEIGHT - 16}
                height={HEADER_HEIGHT - 16}
                style={{
                  objectFit: 'contain',
                  cursor: 'pointer',
                }}
              />
            </IconButton> */}
            <IconButton
              component='a'
              href={
                IS_ETS_NEXT_PROD
                  ? 'https://servicedesk.go.gov/esc'
                  : 'https://gsaetsnexttest.servicenowservices.com/esc'
              }
              target='_blank'
              rel='noopener noreferrer'
              aria-label='Open Service Desk'
              sx={{
                padding: 1,
                // marginRight: 2,
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <Image
                src='/assets/goGovServiceDesk.png'
                alt='Service Desk'
                width={HEADER_HEIGHT - 16}
                height={HEADER_HEIGHT - 16}
                style={{
                  objectFit: 'contain',
                  cursor: 'pointer',
                }}
              />
            </IconButton>
          </Box>
          {isImpersonating && (
            <Typography
              variant='caption'
              sx={(theme) => ({
                marginLeft: theme.spacing(2),
                marginRight: theme.spacing(2),
                whiteSpace: 'nowrap',
                ...theme.typography.caption,
              })}
            >
              Impersonating
            </Typography>
          )}
          <Box
            sx={(theme) => ({ height: '100%', marginRight: theme.spacing(4) })}
          >
            <Button
              sx={(theme) => ({
                display: 'inline-flex',
                width: 'auto',
                alignItems: 'center',
                paddingRight: 0,
                height: '100%',
                backgroundColor: 'transparent',
                position: 'relative',
                '&:hover, &:focus, &:active': {
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                },
                '& > *': {
                  margin: theme.spacing(1),
                },
              })}
              onClick={handleOpenMenu}
              color='inherit'
              aria-label='Log out'
            >
              <Avatar
                style={{ backgroundColor: generateColor(user?.lastName) }}
                sx={{
                  transition: 'box-shadow 0.2s',
                  boxShadow: (theme) =>
                    anchorEl
                      ? `0 0 0 4px #fff, 0 0 0 6px ${theme.palette.primary.main}`
                      : 'none',
                  '&:hover, &:focus': {
                    boxShadow: (theme) =>
                      `0 0 0 4px #fff, 0 0 0 6px ${theme.palette.primary.main}`,
                  },
                }}
              >
                {`${user?.firstName?.[0]}${user?.lastName?.[0]}`}
              </Avatar>
            </Button>

            <Menu
              id='account-menu'
              anchorEl={anchorEl}
              open={!!anchorEl}
              onClose={closeMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              slotProps={{
                paper: {
                  sx: (theme) => ({
                    ml: -5,
                    boxShadow: `0 0 2px 0 ${theme.palette.grey[500]}40, -20px 20px 40px -4px ${theme.palette.grey[500]}40`,
                    bgcolor: theme.palette.background.paper,
                  }),
                },
              }}
            >
              {isImpersonating && (
                <MenuItem
                  onClick={() => clearImpersonatedUser()}
                  sx={{ px: 0, py: 0 }}
                >
                  <Box
                    sx={(theme) => ({
                      px: theme.spacing(2),
                      py: theme.spacing(1.5),
                      display: 'flex',
                      alignItems: 'center',
                      height: '100%',
                      width: '100%',
                    })}
                  >
                    Stop Impersonating User
                  </Box>
                </MenuItem>
              )}
              <MenuItem onClick={logoutClick} sx={{ px: 0, py: 0 }}>
                <Box
                  sx={(theme) => ({
                    px: theme.spacing(2),
                    py: theme.spacing(1.5),
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                  })}
                >
                  <ListItemText>Log out</ListItemText>
                </Box>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </AppBar>
    </CustomThemeProvider>
  );
}

export default GoGovHeaderBar;
